import { Calendar, Check, ChevronDown, MapPin } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Sheet from '../ui/Sheet';
import { btnGhost, btnPrimary, btnPrimarySm, focusRing, textLink } from '../ui/redesign';

import { useRegionText, useWindowText } from './filterText';
import { HOME_REGIONS, HOME_TIMES, type HomeRegion, type HomeSelection } from './homeState';

const isDesk = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(min-width: 761px)').matches === true;

const pillBase = `inline-flex min-h-[44px] min-w-0 items-center gap-1.5 desk:gap-2 rounded-full border-[1.5px] px-3.5 py-2 font-ui text-sm text-ink ${focusRing}`;
const optBase = `flex min-h-[44px] w-full items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-left text-[14.5px] hover:bg-warm-white ${focusRing}`;

function OptBox({ on, multi }: { on: boolean; multi?: boolean }) {
  return (
    <span
      className={`grid h-[18px] w-[18px] flex-none place-items-center border-[1.5px] ${
        multi ? 'rounded-[5px]' : 'rounded-full'
      } ${on ? 'border-primary bg-primary text-white' : 'border-line'}`}
    >
      {on && <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />}
    </span>
  );
}

/**
 * Hero filter row (prototype `.frow`): "时间" and "地点" pills and the browse link.
 * Desktop opens a dropdown under the pill; phones open the bottom sheet instead.
 */
export function HomeFilterRow({
  value,
  onChange,
  onOpenSheet,
}: {
  value: HomeSelection;
  onChange: (next: HomeSelection) => void;
  onOpenSheet: () => void;
}) {
  const { t, i18n } = useTranslation(['home']);
  const windowText = useWindowText();
  const regionText = useRegionText();
  // Phones: the chosen value matters more than "时间 / 地点", and the icon already says
  // which pill is which. English words are longer, so English drops the label there.
  const keyLabel = `whitespace-nowrap font-medium text-mute max-[374px]:hidden ${
    i18n.language.startsWith('en') ? 'hidden desk:inline' : ''
  }`;
  const [pop, setPop] = useState<'time' | 'place' | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pop) return;
    const onDown = (e: MouseEvent) => {
      if (!rowRef.current?.contains(e.target as Node)) setPop(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPop(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pop]);

  const open = (which: 'time' | 'place') => {
    if (!isDesk()) {
      setPop(null);
      onOpenSheet();
      return;
    }
    setPop((cur) => (cur === which ? null : which));
  };

  const toggleRegion = (r: HomeRegion) =>
    onChange({
      ...value,
      regions: value.regions.includes(r)
        ? value.regions.filter((x) => x !== r)
        : HOME_REGIONS.filter((x) => x === r || value.regions.includes(x)),
    });

  return (
    <div
      ref={rowRef}
      className="relative mt-2.5 flex flex-wrap items-center gap-2 desk:mt-4 desk:gap-2.5"
    >
      <button
        type="button"
        aria-expanded={pop === 'time'}
        onClick={() => open('time')}
        className={`${pillBase} flex-1 justify-center px-2.5 desk:flex-none desk:justify-start desk:px-3.5 ${
          value.when !== 'anytime' ? 'border-primary bg-primary-pale' : 'border-line bg-white'
        }`}
      >
        <Calendar className="h-4 w-4 flex-none text-primary" aria-hidden="true" />
        <span className={keyLabel}>{t('filters.time')}</span>
        <span className="min-w-0 truncate whitespace-nowrap font-semibold">
          {t(`filters.times.${value.when}`)}
        </span>
        <ChevronDown className="h-4 w-4 flex-none text-mute" aria-hidden="true" />
      </button>
      <button
        type="button"
        aria-expanded={pop === 'place'}
        onClick={() => open('place')}
        className={`${pillBase} min-w-0 flex-1 justify-center px-2.5 desk:flex-none desk:justify-start desk:px-3.5 ${
          value.regions.length > 0 ? 'border-primary bg-primary-pale' : 'border-line bg-white'
        }`}
      >
        <MapPin className="h-4 w-4 flex-none text-primary" aria-hidden="true" />
        <span className={keyLabel}>{t('filters.place')}</span>
        <span className="min-w-0 truncate font-semibold">{regionText(value.regions)}</span>
        <ChevronDown className="h-4 w-4 flex-none text-mute" aria-hidden="true" />
      </button>
      <Link to="/activities" className={`${textLink} hidden desk:inline-flex`}>
        {t('hero.browseAll')}
      </Link>

      {pop === 'time' && (
        <div
          role="dialog"
          aria-label={t('filters.time')}
          className="absolute left-0 top-[calc(100%+6px)] z-[35] min-w-[240px] rounded-2xl border border-line bg-white p-2.5 shadow-[0_22px_50px_-24px_rgba(0,0,0,0.45)]"
        >
          {HOME_TIMES.map((w) => (
            <button
              key={w}
              type="button"
              role="menuitemradio"
              aria-checked={value.when === w}
              className={optBase}
              onClick={() => {
                onChange({ ...value, when: w });
                setPop(null);
              }}
            >
              <OptBox on={value.when === w} />
              {t(`filters.times.${w}`)}
              {w !== 'anytime' && (
                <small className="ml-auto text-[12px] text-mute">{windowText(w)}</small>
              )}
            </button>
          ))}
        </div>
      )}
      {pop === 'place' && (
        <div
          role="dialog"
          aria-label={t('filters.place')}
          className="absolute left-0 top-[calc(100%+6px)] z-[35] min-w-[240px] rounded-2xl border border-line bg-white p-2.5 shadow-[0_22px_50px_-24px_rgba(0,0,0,0.45)]"
        >
          {HOME_REGIONS.map((r) => (
            <RegionOption
              key={r}
              region={r}
              on={value.regions.includes(r)}
              onToggle={() => toggleRegion(r)}
            />
          ))}
          <div className="mt-1.5 flex items-center justify-between gap-2.5 pl-3">
            <button
              type="button"
              className={textLink}
              onClick={() => onChange({ ...value, regions: [] })}
            >
              {t('filters.clear')}
            </button>
            <button type="button" className={btnPrimarySm} onClick={() => setPop(null)}>
              {t('filters.done')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function RegionOption({
  region,
  on,
  onToggle,
}: {
  region: HomeRegion;
  on: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation('common');
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={on}
      className={optBase}
      onClick={onToggle}
    >
      <OptBox on={on} multi />
      {t(`region.${region}`)}
    </button>
  );
}

const chip = `inline-flex min-h-[44px] items-center gap-1.5 rounded-full border-[1.5px] px-3.5 py-2 font-ui text-sm font-medium ${focusRing} aria-pressed:border-primary aria-pressed:bg-primary-pale aria-pressed:font-semibold aria-pressed:text-primary-dark border-line bg-white text-ink-2`;

/**
 * Phone "时间和地点" bottom sheet (PRD §3.6): chips change the selection right away;
 * "查看推荐" closes the sheet and scrolls to the recommendations.
 */
export function HomeFilterSheet({
  open,
  value,
  onChange,
  onClose,
  onApply,
}: {
  open: boolean;
  value: HomeSelection;
  onChange: (next: HomeSelection) => void;
  onClose: () => void;
  onApply: () => void;
}) {
  const { t } = useTranslation(['home', 'common']);
  const windowText = useWindowText();
  return (
    <Sheet
      open={open}
      onClose={onClose}
      closeLabel={t('common:share.close')}
      title={t('home:filters.sheetTitle')}
    >
      <h4 className="mb-2.5 mt-[18px] font-ui text-[13px] font-semibold tracking-[0.04em] text-mute">
        {t('home:filters.time')}
      </h4>
      <div className="flex flex-wrap gap-2">
        {HOME_TIMES.map((w) => (
          <button
            key={w}
            type="button"
            aria-pressed={value.when === w}
            className={chip}
            onClick={() => onChange({ ...value, when: w })}
          >
            {t(`home:filters.times.${w}`)}
            {w !== 'anytime' && (
              <small className="text-[11.5px] font-normal text-mute">{windowText(w)}</small>
            )}
          </button>
        ))}
      </div>
      <h4 className="mb-2.5 mt-[18px] flex items-baseline gap-2 font-ui text-[13px] font-semibold tracking-[0.04em] text-mute">
        {t('home:filters.place')}
        <span className="text-[12.5px] font-normal tracking-normal">
          {t('home:filters.multiHint')}
        </span>
      </h4>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          aria-pressed={value.regions.length === 0}
          className={chip}
          onClick={() => onChange({ ...value, regions: [] })}
        >
          {t('home:filters.allBayArea')}
        </button>
        {HOME_REGIONS.map((r) => (
          <button
            key={r}
            type="button"
            aria-pressed={value.regions.includes(r)}
            className={chip}
            onClick={() =>
              onChange({
                ...value,
                regions: value.regions.includes(r)
                  ? value.regions.filter((x) => x !== r)
                  : HOME_REGIONS.filter((x) => x === r || value.regions.includes(x)),
              })
            }
          >
            {t(`common:region.${r}`)}
          </button>
        ))}
      </div>
      <div className="mt-[18px] flex flex-wrap gap-2.5">
        <button
          type="button"
          className={`${btnGhost} flex-1`}
          onClick={() => onChange({ ...value, when: 'anytime', regions: [] })}
        >
          {t('home:filters.clear')}
        </button>
        <button type="button" className={`${btnPrimary} flex-1`} onClick={onApply}>
          {t('home:filters.apply')}
        </button>
      </div>
    </Sheet>
  );
}
